// Rate Integration Utilities for Calculator Integration
// This file provides functions to get interest rates from the Redux store
// and integrate them with the existing calculator logic

import { type RootState } from '@/store';

import type { HybridBackendRates } from '@/store/slices/hybridInterestRateSlice';
import { type LoanType } from '@/utils/rateMatrixUtils';
import { getCurrentInterestRate as getSheetInterestRate } from '../calculator/rateIntegration';

export { getBridgeRateByTerm } from '../calculator/rateIntegration';

const loanTypeToBackendKey: Record<LoanType, keyof HybridBackendRates> = {
  Cash: 'conventional',
  Conventional: 'conventional',
  FHA: 'fha',
  VA: 'va',
  USDA: 'usda',
  DSCR: 'dscr',
  Commercial: 'commercial',
  FixAndFlip: 'fixAndFlip',
  Bridge: 'bridge',
  HELOC: 'heloc',
  HELOAN: 'heloan',
  SecondMortgage: 'secondMortgage',
};

// Function to get the current interest rate from Redux store
export const getCurrentInterestRate = (
  state: RootState,
  loanType: LoanType,
  creditScore: number,
  ltv: number
): number => {
  const rateMatrices = state.interestRate.rateMatrices;
  const matricesLoaded = state.interestRate.matricesLoaded;
  const matrix = matricesLoaded ? rateMatrices[loanType] : null;

  if (!matrix) {
    // Fallback to backend rates if matrix not found
    return getDefaultRateForLoanType(state, loanType);
  }

  // Find the appropriate rate based on credit score and LTV
  const rate = findRateFromMatrix(matrix, creditScore, ltv);

  return rate ?? getDefaultRateForLoanType(state, loanType);
};

// Helper function to find rate from matrix
const findRateFromMatrix = (
  matrix: any,
  creditScore: number,
  ltv: number
): number | null => {
  if (!matrix.matrix || !Array.isArray(matrix.matrix)) {
    return null;
  }

  // Find the closest credit score and LTV ranges
  const config = matrix.config;
  if (!config) {
    return null;
  }

  // Find credit score index
  let creditScoreIndex = -1;
  for (let i = 0; i < config.xAxis.values.length; i++) {
    if (creditScore >= config.xAxis.values[i]) {
      creditScoreIndex = i;
    }
  }

  // Find LTV index
  let ltvIndex = -1;
  for (let i = 0; i < config.yAxis.values.length; i++) {
    if (ltv <= config.yAxis.values[i]) {
      ltvIndex = i;
    }
  }

  // Get rate from matrix
  if (
    creditScoreIndex >= 0 &&
    ltvIndex >= 0 &&
    matrix.matrix[ltvIndex] &&
    matrix.matrix[ltvIndex][creditScoreIndex]
  ) {
    return matrix.matrix[ltvIndex][creditScoreIndex].rate;
  }

  return null;
};

// Default rates for each loan type (fallback)
const getDefaultRateForLoanType = (
  state: RootState,
  loanType: LoanType
): number => {
  const backendRates = state.hybridInterestRate?.backendRates;
  const backendKey = loanTypeToBackendKey[loanType];

  if (backendRates && backendKey) {
    const candidate = backendRates[backendKey];
    if (typeof candidate === 'number') {
      return candidate;
    }
  }

  const defaultRates: Record<LoanType, number> = {
    Cash: 0,
    Conventional: 6.75,
    FHA: 6.5,
    VA: 6.25,
    USDA: 6.0,
    DSCR: 7.5,
    Commercial: 8.0,
    FixAndFlip: 9.0,
    Bridge: 10.5,
    HELOC: 7.75,
    HELOAN: 7.25,
    SecondMortgage: 8.75,
  };

  return defaultRates[loanType] || 6.75;
};

// Enhanced interest rate calculation that uses the Redux store
export const calculateEnhancedInterestRate = (
  state: RootState,
  loanType: LoanType,
  baseRate: number,
  creditScore: number,
  ltv: number,
  loanTerm?: number
): number => {
  // Primary: the live daily rate sheet, priced by the shared engine — the
  // same rate purchase estimates and the backend quote. (Refinance estimates
  // previously only used admin-page matrices, which buyers never load.)
  if (state?.hybridInterestRate?.rateSheet) {
    const sheetRate = getSheetInterestRate(
      state,
      loanType,
      creditScore,
      ltv,
      loanTerm
    );
    if (Number.isFinite(sheetRate)) return sheetRate;
  }

  // Fallback: the interest rate system (admin matrices / base rates)
  const systemRate = getCurrentInterestRate(state, loanType, creditScore, ltv);

  if (systemRate !== getDefaultRateForLoanType(state, loanType)) {
    // If we got a rate from the system, use it
    return systemRate;
  }

  // Fallback to the original calculation logic
  return calculateFallbackInterestRate(loanType, baseRate, creditScore, ltv);
};

// Fallback calculation logic (original calculator logic)
const calculateFallbackInterestRate = (
  loanType: LoanType,
  baseRate: number,
  creditScore: number,
  ltv: number
): number => {
  let rateAdjustment = 0;

  // Credit score adjustments
  if (creditScore >= 760) {
    rateAdjustment -= 0.25;
  } else if (creditScore >= 740) {
    rateAdjustment -= 0.125;
  } else if (creditScore >= 720) {
    rateAdjustment -= 0.0625;
  } else if (creditScore >= 700) {
    // No change
  } else if (creditScore >= 680) {
    rateAdjustment += 0.125;
  } else if (creditScore >= 660) {
    rateAdjustment += 0.25;
  } else if (creditScore >= 640) {
    rateAdjustment += 0.375;
  } else if (creditScore >= 620) {
    rateAdjustment += 0.5;
  } else if (creditScore >= 600) {
    rateAdjustment += 0.625;
  } else if (creditScore >= 580) {
    rateAdjustment += 0.75;
  } else if (creditScore >= 560) {
    rateAdjustment += 0.875;
  } else if (creditScore >= 540) {
    rateAdjustment += 1.0;
  } else if (creditScore >= 520) {
    rateAdjustment += 1.125;
  } else {
    rateAdjustment += 1.25;
  }

  // LTV adjustments
  if (ltv > 0.95) {
    rateAdjustment += 0.375;
  } else if (ltv > 0.9) {
    rateAdjustment += 0.25;
  } else if (ltv > 0.85) {
    rateAdjustment += 0.125;
  } else if (ltv > 0.8) {
    rateAdjustment += 0.0625;
  } else {
    // LTV <= 80%, no additional adjustment
  }

  return Math.max(0, baseRate + rateAdjustment);
};
